// ============================================
// FAMUZA MARKET — Seller Auth Page
// Signup / Login / Forgot Password
// ============================================

import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('loggedout') === 'true') setLoggedOut(true);
  }, [searchParams]);

  async function handleGoogleLogin() {
    setGoogleLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: 'https://www.famuza.net/seller/dashboard' }
    });
    if (error) { setError(error.message); setGoogleLoading(false); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!isLogin && password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!isLogin && password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    setLoading(true);

    if (isLogin) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) { setError(error.message); setLoading(false); }
      else window.location.href = '/seller/dashboard';
    } else {
      const { error } = await supabase.auth.signUp({
        email, password,
        options: {
          data: { full_name: fullName, phone },
          emailRedirectTo: 'https://www.famuza.net/seller/dashboard'
        }
      });
      if (error) { setError(error.message); setLoading(false); }
      else { setSignupSuccess(true); setLoading(false); }
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://www.famuza.net/reset-password'
    });
    if (error) { setError(error.message); setLoading(false); }
    else { setResetSent(true); setLoading(false); }
  }

  // Logged out screen
  if (loggedOut) {
    return (
      <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>👋</div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.75rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.75rem' }}>You've been logged out</h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.7, marginBottom: '1.5rem' }}>Thanks for selling on FAMUZA Market. See you soon!</p>
          <button onClick={() => { setIsLogin(true); setLoggedOut(false); }}
            style={{ width: '100%', padding: '0.875rem', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', marginBottom: '0.75rem' }}>
            Log back in
          </button>
          <Link to="/" style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>← Back to FAMUZA Market</Link>
        </div>
      </div>
    );
  }

  // Signup success screen
  if (signupSuccess) {
    return (
      <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📧</div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.75rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.75rem' }}>Check your email!</h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
            We sent a confirmation link to <strong style={{ color: '#E8622A' }}>{email}</strong>. Click the link to activate your account.
          </p>
          <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1rem', marginBottom: '1.5rem', fontSize: '0.82rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.7 }}>
            💡 Can't find it? Check your spam/junk folder.
          </div>
          <button onClick={() => { setIsLogin(true); setSignupSuccess(false); }}
            style={{ width: '100%', padding: '0.875rem', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', marginBottom: '0.75rem' }}>
            Go to Login
          </button>
          <Link to="/" style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>← Back to FAMUZA Market</Link>
        </div>
      </div>
    );
  }

  // Reset sent screen
  if (resetSent) {
    return (
      <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📩</div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.75rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.75rem' }}>Reset link sent!</h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(245,240,232,0.6)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
            We sent a password reset link to <strong style={{ color: '#E8622A' }}>{email}</strong>. Click the link in the email to set a new password.
          </p>
          <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '1rem', marginBottom: '1.5rem', fontSize: '0.82rem', color: 'rgba(245,240,232,0.5)', lineHeight: 1.7 }}>
            💡 Can't find it? Check your spam/junk folder.
          </div>
          <button onClick={() => { setForgotPassword(false); setResetSent(false); setIsLogin(true); }}
            style={{ width: '100%', padding: '0.875rem', background: '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', marginBottom: '0.75rem' }}>
            Back to Login
          </button>
          <Link to="/" style={{ fontSize: '0.85rem', color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>← Back to FAMUZA Market</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ maxWidth: 420, width: '100%' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.5rem', fontWeight: 800, color: '#F5F0E8', marginBottom: '0.25rem' }}>
              FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
            </div>
          </Link>
          <div style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.4)' }}>
            {forgotPassword ? 'Reset your password' : isLogin ? 'Welcome back — log in to your seller account' : 'Create your seller account — it\'s free'}
          </div>
        </div>

        {/* Card */}
        <div style={{ background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '2rem' }}>

          {/* Forgot Password Form */}
          {forgotPassword ? (
            <>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '0.5rem' }}>Forgot Password</h2>
              <p style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.5)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
                Enter your email and we'll send you a link to reset your password.
              </p>
              <form onSubmit={handleForgotPassword}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                {error && (
                  <div style={{ background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 8, padding: '0.75rem', fontSize: '0.82rem', color: '#E8622A', marginBottom: '1rem' }}>
                    ⚠️ {error}
                  </div>
                )}
                <button type="submit" disabled={loading}
                  style={{ width: '100%', padding: '0.9rem', background: loading ? '#333' : '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '1rem' }}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => { setForgotPassword(false); setError(''); }}
                  style={{ width: '100%', padding: '0.75rem', background: 'transparent', color: 'rgba(245,240,232,0.5)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
                  ← Back to Login
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Tab switcher */}
              <div style={{ display: 'flex', background: '#0D0D0D', borderRadius: 10, padding: 4, marginBottom: '1.5rem' }}>
                {['Login', 'Sign Up'].map((tab, i) => (
                  <button key={tab} onClick={() => { setIsLogin(i === 0); setError(''); }}
                    style={{ flex: 1, padding: '0.6rem', background: (isLogin ? i === 0 : i === 1) ? '#E8622A' : 'transparent', color: (isLogin ? i === 0 : i === 1) ? '#fff' : 'rgba(245,240,232,0.5)', border: 'none', borderRadius: 8, fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                    {tab}
                  </button>
                ))}
              </div>

              {/* Google Login */}
              <button onClick={handleGoogleLogin} disabled={googleLoading}
                style={{ width: '100%', padding: '0.8rem', background: '#fff', color: '#1a1a1a', border: 'none', borderRadius: 12, fontSize: '0.95rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {googleLoading ? 'Redirecting...' : 'Continue with Google'}
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.1)' }} />
                <span style={{ fontSize: '0.75rem', color: 'rgba(245,240,232,0.3)', fontFamily: 'DM Sans, sans-serif' }}>or continue with email</span>
                <div style={{ flex: 1, height: '0.5px', background: 'rgba(255,255,255,0.1)' }} />
              </div>

              <form onSubmit={handleSubmit}>
                {!isLogin && (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Full Name</label>
                      <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} required placeholder="e.g. Gilbert Mumba"
                        style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Phone / WhatsApp</label>
                      <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. 260977123456"
                        style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com"
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                <div style={{ marginBottom: isLogin ? '0.5rem' : '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Minimum 6 characters"
                    onKeyUp={e => setCapsLock(e.getModifierState('CapsLock'))}
                    onFocus={e => setCapsLock(e.nativeEvent.getModifierState('CapsLock'))}
                    style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                  {capsLock && (
                    <div style={{ fontSize: '0.75rem', color: '#C49A2A', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      ⚠️ Caps Lock is on
                    </div>
                  )}
                </div>

                {/* Forgot password link — only on login tab */}
                {isLogin && (
                  <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
                    <button type="button" onClick={() => { setForgotPassword(true); setError(''); }}
                      style={{ background: 'none', border: 'none', color: '#E8622A', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                      Forgot password?
                    </button>
                  </div>
                )}

                {!isLogin && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(245,240,232,0.6)', marginBottom: '0.4rem' }}>Confirm Password</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Repeat your password"
                      style={{ width: '100%', padding: '0.75rem 1rem', background: '#0D0D0D', border: `0.5px solid ${confirmPassword && confirmPassword !== password ? 'rgba(232,98,42,0.6)' : confirmPassword && confirmPassword === password ? 'rgba(37,211,102,0.6)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 10, color: '#F5F0E8', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box' }} />
                    {confirmPassword && confirmPassword !== password && <div style={{ fontSize: '0.75rem', color: '#E8622A', marginTop: '0.3rem' }}>Passwords don't match</div>}
                    {confirmPassword && confirmPassword === password && <div style={{ fontSize: '0.75rem', color: '#25D366', marginTop: '0.3rem' }}>✓ Passwords match</div>}
                  </div>
                )}

                {error && (
                  <div style={{ background: 'rgba(232,98,42,0.1)', border: '0.5px solid rgba(232,98,42,0.3)', borderRadius: 8, padding: '0.75rem', fontSize: '0.82rem', color: '#E8622A', marginBottom: '1rem' }}>
                    ⚠️ {error}
                  </div>
                )}

                <button type="submit" disabled={loading}
                  style={{ width: '100%', padding: '0.9rem', background: loading ? '#333' : '#E8622A', color: '#fff', border: 'none', borderRadius: 12, fontSize: '1rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', marginTop: '0.5rem' }}>
                  {loading ? 'Please wait...' : isLogin ? 'Log In' : 'Create Account'}
                </button>
              </form>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Link to="/" style={{ fontSize: '0.82rem', color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>← Back to FAMUZA Market</Link>
        </div>
      </div>
    </div>
  );
}
